import {
  Outlet,
  NavLink,
  useLocation as useRouterLocation,
  Link,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  BookOpen,
  FileCheck,
  Scale,
  Calculator,
  TrendingUp,
  Menu,
  X,
  Home,
  Phone,
  Mail,
  ExternalLink,
  Newspaper,
} from "lucide-react";
import { useState } from "react";
import {
  LocationSelector,
  LocationNotSupportedBanner,
} from "./LocationSelector";
import { useLocation } from "../contexts/LocationContext";

const navItems = [
  { path: "/chat", label: "Chat", icon: MessageCircle },
  { path: "/explore", label: "Code Library", icon: BookOpen },
  { path: "/permits", label: "Permits", icon: FileCheck },
  { path: "/sdab", label: "SDAB Expert", icon: Scale },
  { path: "/quantity-survey", label: "Quantity Survey", icon: Calculator },
  { path: "/feasibility", label: "Feasibility Study", icon: TrendingUp },
  { path: "/blog", label: "Blog", icon: Newspaper },
];

export function Layout() {
  const routerLocation = useRouterLocation();
  useLocation(); // Initialize location context
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex flex-col">
      {/* Location Not Supported Banner */}
      <LocationNotSupportedBanner />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-teal-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - Links to Home */}
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                  BB
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Builder Buddy
                  </h1>
                  <p className="text-xs text-gray-500">
                    Canada's Building Code Companion
                  </p>
                </div>
              </Link>
              {/* Location Selector */}
              <div className="hidden sm:block">
                <LocationSelector variant="header" />
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {/* Home Button */}
              <Link
                to="/"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-colors border border-transparent hover:border-teal-200"
              >
                <Home className="h-4 w-4" />
                <span>Home</span>
              </Link>
              <div className="w-px h-6 bg-gray-200 mx-1" />
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-teal-100 text-teal-700"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-100"
            >
              <nav className="px-4 py-2 space-y-1">
                {/* Location Selector for Mobile */}
                <div className="mb-3">
                  <LocationSelector variant="full" />
                </div>
                <div className="border-b border-gray-200 my-2" />
                {/* Home Button */}
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 transition-colors"
                >
                  <Home className="h-5 w-5" />
                  <span>Back to Home</span>
                </Link>
                <div className="border-b border-gray-200 my-2" />
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-teal-100 text-teal-700"
                          : "text-gray-600 hover:bg-gray-100"
                      }`
                    }
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={routerLocation.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="mt-auto py-8 border-t border-teal-100 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {/* Branding */}
            <div>
              <p className="text-sm text-gray-600 font-medium">
                Builder Buddy is a subsidiary of{" "}
                <a
                  href="https://tamcrete-eng.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:text-teal-700 hover:underline inline-flex items-center gap-1"
                >
                  Tam-Crete Engineering
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                All prices in CAD. Data sourced from official government
                publications.
              </p>
            </div>

            {/* Resources */}
            <div className="md:justify-self-center">
              <p className="text-sm text-gray-600 font-medium mb-2">
                Resources
              </p>
              <div className="flex flex-col gap-2">
                <Link
                  to="/blog"
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-teal-600 transition-colors"
                >
                  <Newspaper className="w-4 h-4" />
                  Blog
                </Link>
                <Link
                  to="/privacy"
                  className="text-sm text-gray-600 hover:text-teal-600 transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-sm text-gray-600 hover:text-teal-600 transition-colors"
                >
                  Terms of Service
                </Link>
              </div>
            </div>

            {/* Contact Info */}
            <div className="md:justify-self-end">
              <p className="text-sm text-gray-600 font-medium mb-2">
                Support & Questions
              </p>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:gap-2">
                <a
                  href="tel:+14033335025"
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-teal-600 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  (403) 333-5025
                </a>
                <a
                  href="mailto:Tam.Crete.engineering@gmail.com"
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-teal-600 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Contact Us
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} Builder Buddy Canada. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
